import React, { useEffect, useState, useMemo, useContext } from "react";
import api from "../api/axios";
import toast from "react-hot-toast";
import { CheckCircle, Trash2, Award, Calendar } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import { LoadingCard } from "../components/LoadingSpinner";
// import css
import "./MyCert.css";

/* ========= Reusable Modal Component ========= */
function Modal({ show, onClose, children }) {
  if (!show) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
  <div style={{ marginBottom: "20px" }}>
    {children}
  </div>

  <div style={styles.modalFooter}>
    <button className="btn btn-secondary" onClick={onClose}>
      Close
    </button>
  </div>
</div>

    </div>
  );
}

export default function MyCertifications() {
  const { user, isAuthenticated, authenticationInProgress, loading } = useContext(AuthContext);
  const [catalog, setCatalog] = useState([]);
  const [myCerts, setMyCerts] = useState([]);
  const [selectedCert, setSelectedCert] = useState("");
  const [completing, setCompleting] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  /* 🔸 Delete modal */
  const [deleteModal, setDeleteModal] = useState({ open: false, certId: null });

  /* 🔸 Complete modal */
  const [completeModal, setCompleteModal] = useState({
    open: false,
    certId: null,
    provider: "",
    certValue: "",
  });

  const [filterStatus, setFilterStatus] = useState("All");

  useEffect(() => {
    // Only load data when user is fully authenticated
    if (!isAuthenticated || authenticationInProgress || loading || !user) {
      return;
    }
    loadData();
  }, [isAuthenticated, authenticationInProgress, loading, user]);

  async function loadData() {
    try {
      setDataLoading(true);
      const [catRes, certRes] = await Promise.all([
        api.get("/certs/catalog"),
        api.get("/certs/my"),
      ]);
      setCatalog(catRes.data);
      setMyCerts(certRes.data);
    } catch (err) {
      console.error("Failed to load certifications:", err);
      toast.error("Failed to load certifications");
    } finally {
      setDataLoading(false);
    }
  }

  async function registerCert() {
    if (!selectedCert) return toast.error("Select a certificate");

    try {
      setRegistering(true);
      await api.post("/certs/register", { certId: selectedCert });
      toast.success("Certificate registered!");
      loadData();
      setSelectedCert("");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Registration failed");
    }
    finally {
      setRegistering(false); // ⬅️ stop loading
    }
  }

  /* ---------- DELETE CONFIRMATION ---------- */
  async function deleteCertConfirmed() {
    try {
      await api.delete(`/certs/${deleteModal.certId}`);
      toast.success("Deleted");
      loadData();
      setDeleteModal({ open: false, certId: null });
    } catch {
      toast.error("Failed to delete certificate");
    }
  }

  /* ---------- COMPLETE CERT ---------- */
  async function completeCertConfirmed() {
    const { certId, provider, certValue } = completeModal;

    // Validation only for AWS / Azure / NVIDIA
    if (provider === "AWS" && !certValue.trim())
      return toast.error("Please enter AWS Validation Number");

    if (provider === "Microsoft" && !certValue.trim())
      return toast.error("Please enter Azure Certificate URL");

    if (provider === "NVIDIA" && !certValue.trim())
      return toast.error("Please enter Credly Badge Public URL");

    try {
      setCompleting(true);
       await api.post("/certs/complete", {
        certId,
        extraValue: certValue, // backend can use this
      });
      toast.success("Marked as completed!");
      loadData();
      setCompleteModal({ open: false, certId: null, provider: "", certValue: "" });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to complete certificate";
      toast.error(msg);
    }
    finally {
      setCompleting(false); // ⬅️ stop loading
    }
  }

  /* ---------- FILTER ---------- */
  const filtered = useMemo(() => {
    return myCerts.filter((c) => {
      if (filterStatus !== "All" && c.status !== filterStatus) return false;
      return true;
    });
  }, [myCerts, filterStatus]);

  const certMap = {};
  catalog.forEach((c) => (certMap[c.certId] = c));

  // Show loading state while data is being fetched
  if (dataLoading) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <LoadingCard message="Loading your certifications..." />
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ color: "#eaf6ff" }}>
      {/* ---------- Register Section ---------- */}
      <div style={styles.card}>
        <h4 style={{ fontWeight: 700, marginBottom: 12 }}>Register Certification</h4>

        <div className="d-flex gap-3 register-row">
          <select
            className="form-select"
            value={selectedCert}
            onChange={(e) => setSelectedCert(e.target.value)}
            style={{ maxWidth: 450 }}
          >
            <option value="">Select certificate…</option>
            {[...catalog]
              .sort((a, b) => {
                // Sort by provider first, then by name
                const providerCompare = a.provider.localeCompare(b.provider);
                return providerCompare !== 0 ? providerCompare : a.name.localeCompare(b.name);
              })
              .map((c) => (
                <option key={c.certId} value={c.certId}>
                  {c.name} ({c.provider})
                </option>
              ))}
          </select>

          {/* <button className="btn btn-primary px-4" onClick={registerCert} style={{ borderRadius: 10 }}>
            Add to Quest
          </button> */}
          <button
  className="btn btn-primary px-4 d-flex align-items-center justify-content-center"
  onClick={registerCert}
  disabled={registering}
  style={{ borderRadius: 10, minWidth: "150px", height: "40px" }}
>
  {registering ? (
    <>
      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
      Registering...
    </>
  ) : (
    "Register Now →"
  )}
</button>
        </div>

        <div style={{ marginTop: 8, color: "#8ecbff", fontSize: 13 }}>
          Register → Earn points •• Complete → earns more points!
        </div>
      </div>

      {/* ---------- MY CERTIFICATIONS ---------- */}
      <div style={styles.card}>
        <div className="d-flex justify-content-between align-items-center mb-3 filter-row">
          <h5 style={{ margin: 0 }}>My Certifications</h5>

          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ maxWidth: 180 }}
          >
            <option value="All">All Status</option>
            <option value="Registered">Registered</option>
            <option value="Completed">Completed</option>
          </select>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center" style={{ padding: 30, color: "#8ecbff", fontSize: 15 }}>
            No certifications found
          </div>
        ) : (
          <div className="row g-3">
            {filtered.map((item) => {
              const meta = certMap[item.certId];

              return (
                <div key={item.certId} className="col-lg-6 cert-col">
                  <div style={styles.certCard}>
                    <div style={{ fontWeight: 700, fontSize: 17 }}>{meta?.name || item.certId}</div>

                    <div className="mt-1" style={{ color: "#8ecbff", fontSize: 13 }}>
                      {meta?.provider}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <StatusBadge status={item.status} />

                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 13, color: "#8ecbff" }}>
                          Points: <span style={{ color: "#fff" }}>{item.points}</span>
                        </div>

                        {item.completedDate && (
                          <div style={{ fontSize: 12, color: "#8ecbff" }}>
                            <Calendar size={14} style={{ marginRight: 4 }} />
                            {item.completedDate}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="d-flex gap-2 mt-3">
                      {item.status === "Registered" ? (
                        <>
                          <button
                            className="btn btn-success btn-sm"
                            style={{ borderRadius: 8, fontWeight: 600 }}
                            onClick={() =>
                              setCompleteModal({
                                open: true,
                                certId: item.certId,
                                provider: meta?.provider,
                                certValue: "",
                              })
                            }
                          >
                            <CheckCircle size={15} /> Complete
                          </button>

                          <button
                            className="btn btn-outline-danger btn-sm"
                            style={{ borderRadius: 8 }}
                            onClick={() =>
                              setDeleteModal({
                                open: true,
                                certId: item.certId,
                              })
                            }
                          >
                            <Trash2 size={15} />
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ---------- DELETE MODAL ---------- */}
      <Modal show={deleteModal.open} onClose={() => setDeleteModal({ open: false })}>
        <h4 style={{ color: "#fff" }}>Confirm Delete</h4>
        <p style={{ color: "#8ecbff" }}>Are you sure you want to delete this certificate?</p>

        <button className="btn btn-danger me-2" onClick={deleteCertConfirmed}>
          Yes, Delete
        </button>
      </Modal>

      {/* ---------- COMPLETE MODAL (AWS / Azure / Others) ---------- */}
      <Modal show={completeModal.open} onClose={() => setCompleteModal({ open: false })}>
        <h4 style={{ color: "#fff" }}>Mark as Completed</h4>

        {completeModal.provider === "AWS" && (
          <>
            <p style={{ color: "#8ecbff" }}>
              Enter your <strong>AWS Validation Number </strong>.
              <br />
              👉 Find it at: <em>AWS Certification → Certifications → Certification Status → Download Certificate → Validation Number </em>
            </p>
            <input
              type="text"
              placeholder="AWS Validation Number"
              value={completeModal.certValue}
              onChange={(e) => setCompleteModal({ ...completeModal, certValue: e.target.value })}
              className="form-control"
            />
          </>
        )}

        {completeModal.provider === "Microsoft" && (
          <>
            <p style={{ color: "#8ecbff" }}>
              Enter your <strong> Azure Certificate URL</strong>.
              <br />
              👉 Find it at: <em>Microsoft Learn → Profile → Credentials → View Certificate detail → Share link → Copy Link</em>
            </p>
            <input
              type="text"
              placeholder="Azure Certificate URL"
              value={completeModal.certValue}
              onChange={(e) => setCompleteModal({ ...completeModal, certValue: e.target.value })}
              className="form-control"
            />
          </>
        )}

        {completeModal.provider === "NVIDIA" && (
          <>
            <p style={{ color: "#8ecbff" }}>
              Enter your <strong>Credly Badge Public URL</strong>.
              <br />
              👉 Find it at: <em>Credly Badge → Share → Copy public URL</em>
            </p>
            <input
              type="text"
              placeholder="https://www.credly.com/badges/YOUR_BADGE_ID/public_url"
              value={completeModal.certValue}
              onChange={(e) => setCompleteModal({ ...completeModal, certValue: e.target.value })}
              className="form-control"
            />
          </>
        )}

        {completeModal.provider !== "AWS" && completeModal.provider !== "Azure" && completeModal.provider !== "NVIDIA" && (
          <p style={{ color: "#8ecbff" }}>
            Confirm marking this <strong>{completeModal.provider}</strong> certification as completed?
          </p>
        )}

        {/* <button className="btn btn-success mt-2" onClick={completeCertConfirmed}>
          Confirm Complete
        </button> */}
        <button
  className="btn btn-success mt-2 d-flex align-items-center justify-content-center"
  onClick={completeCertConfirmed}
  disabled={completing}
  style={{ minWidth: "150px", height: "38px", borderRadius: "8px" }}
>
  {completing ? (
    <>
      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
      Verifying...
    </>
  ) : (
    "Confirm Complete"
  )}
</button>
      </Modal>
    </div>
  );
}

/* ---------- STATUS BADGE ---------- */
function StatusBadge({ status }) {
  if (status === "Completed")
    return (
      <span className="badge bg-success" style={styles.badge}>
        Completed
      </span>
    );

  return (
    <span className="badge bg-warning text-dark" style={styles.badge}>
      Registered
    </span>
  );
}

/* ---------- Styles ---------- */
const styles = {
  card: {
    height : "50%",
    background: "rgba(255,255,255,0.03)",
    borderRadius: 16,
    border: "1px solid rgba(130,200,255,0.15)",
    padding: 20,
    marginBottom: 20,
  },
  certCard: {
    background: "rgba(255,255,255,0.04)",
    padding: 18,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.05)",
  },
  badge: {
    fontSize: 13,
    padding: "6px 10px",
    borderRadius: 8,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2000,
  },
  modal: {
    background: "rgba(3,19,40,0.95)",
    padding: 25,
    borderRadius: 14,
    border: "1px solid rgba(130,200,255,0.3)",
    width: "520px",
    maxWidth: "90%",
    color: "#eaf6ff",
  },
  
};
